import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { toast } from "sonner";
import { Shield, ShieldCheck, CreditCard, Bitcoin, Check, TrendingDown, Clock, Lock } from "lucide-react";
import { GuardianStatsGrid, GuardianYearly, GuardianEquityChart, GuardianOps, SystemsComparison } from "../components/Guardian";
import { CryptoModal } from "./Pricing";

export default function Guardian() {
  const { user } = useAuth();
  const { lang } = useLang();
  const it = lang === "it";
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [plan, setPlan] = useState(null);
  const [cryptoOpen, setCryptoOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/guardian/performance").then((r) => setStats(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlan(r.data.find((p) => p.lookup_key === "bund_guardian_12m"))).catch(() => {});
  }, []);

  const checkout = async () => {
    if (!user) { toast.info(it ? "Accedi per continuare" : "Login to continue"); return navigate("/login"); }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "bund_guardian_12m", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const pillars = [
    { Icon: TrendingDown, title: it ? "Stop loss rigido" : "Hard stop loss",
      text: it ? "Ogni operazione nasce con uno stop loss matematico a 2,5 punti: il rischio massimo per trade è sempre definito prima di entrare." : "Every trade is born with a mathematical 2.5-point stop loss: the maximum risk per trade is always defined before entering." },
    { Icon: Check, title: it ? "Take profit costante" : "Constant take profit",
      text: it ? "Ordine limite a 1,5 punti dall'ingresso: profitti piccoli, certi e ripetuti. Oltre l'89% delle operazioni storiche si è chiusa in utile." : "Limit order 1.5 points from entry: small, certain, repeated profits. Over 89% of historical trades closed in profit." },
    { Icon: Lock, title: it ? "Profit lock annuale" : "Yearly profit lock",
      text: it ? "Raggiunti +2.000 € nell'anno, il sistema si ferma e blinda il risultato: il margine di profitto non viene mai rimesso in gioco." : "Once +€2,000 is reached in the year, the system stops and locks the result: the profit margin is never put back at risk." },
    { Icon: Clock, title: it ? "Fuori mercato di default" : "Flat by default",
      text: it ? "Poche operazioni l'anno, anche solo 2: il sistema resta molto spesso fuori mercato, in attesa delle condizioni statisticamente più favorevoli." : "Few trades per year, even just 2: the system stays flat most of the time, waiting for the statistically most favourable conditions." },
    { Icon: Shield, title: it ? "Filtro di regime SMA200" : "SMA200 regime filter",
      text: it ? "Opera solo nella direzione del trend di lungo periodo: mai contro il mercato. È il filtro che ha protetto il capitale nei regimi più difficili." : "Trades only in the direction of the long-term trend: never against the market. It is the filter that protected capital in the hardest regimes." },
    { Icon: ShieldCheck, title: it ? "Circuit breaker" : "Circuit breaker",
      text: it ? "Se l'anno toccasse −6.000 €, l'operatività si sospende fino a gennaio. In 18 anni di storico non è mai scattato." : "If the year ever hit −€6,000, trading suspends until January. It never triggered in 18 years of history." },
  ];

  return (
    <div data-testid="guardian-page">
      <section className="relative overflow-hidden border-b border-sky-500/20 bg-gradient-to-b from-[#0B1220] to-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
          <p className="text-xs tracking-[0.3em] uppercase text-sky-400 font-mono mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4" /> {it ? "Sistema conservativo · La protezione del capitale prima di tutto" : "Conservative system · Capital protection first"}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05]">
            Bund <span className="text-sky-400">Guardian</span>
          </h1>
          <p className="text-base text-slate-300 max-w-2xl mt-6 leading-relaxed">
            {it
              ? "Il trading system sul Bund Future pensato per l'investitore più prudente: in 18 anni di serie storica non ha mai chiuso un anno in perdita. Poche operazioni selezionate, rischio definito su ogni trade, profitto annuale blindato."
              : "The Bund Future trading system designed for the more prudent investor: in 18 years of track record it has never closed a year at a loss. Few selected trades, defined risk on every trade, yearly profit locked in."}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            {[it ? "18 anni · 0 anni in perdita" : "18 years · 0 losing years",
              it ? "Win rate 89%" : "89% win rate",
              it ? "In media 2-3 operazioni l'anno" : "On average 2-3 trades per year",
              it ? "Stop loss su ogni trade" : "Stop loss on every trade"].map((b, i) => (
              <span key={i} className="text-xs font-mono border border-sky-500/40 text-sky-300 px-3 py-1.5 bg-sky-500/5">{b}</span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-10">
            <a href="#guardian-offer" data-testid="guardian-hero-cta"
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-black font-medium rounded px-6 py-3 transition-colors">
              {it ? "Abbonati — solo annuale" : "Subscribe — annual only"}
            </a>
            <Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors underline underline-offset-4">
              {it ? "Confronta con Bund Alpha" : "Compare with Bund Alpha"}
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-10">
        <GuardianStatsGrid stats={stats} />

        <div>
          <h2 className="text-base md:text-lg font-medium text-white mb-4">{it ? "Come protegge il capitale" : "How it protects capital"}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {pillars.map((p, i) => (
              <div key={i} className="border border-sky-500/15 bg-[#0B1220] p-5">
                <p.Icon className="w-5 h-5 text-sky-400 mb-3" />
                <h3 className="font-heading font-bold text-white">{p.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>

        <GuardianYearly />
        <GuardianEquityChart />
        <GuardianOps />

        <div>
          <h2 className="text-base md:text-lg font-medium text-white mb-1">{it ? "Due sistemi, due investitori" : "Two systems, two investors"}</h2>
          <p className="text-xs text-slate-500 mb-4">
            {it ? "Bund Alpha punta al massimo rendimento; Bund Guardian alla costanza. Offerte e abbonamenti separati." : "Bund Alpha targets maximum return; Bund Guardian targets consistency. Separate offers and subscriptions."}
          </p>
          <SystemsComparison />
        </div>

        <div id="guardian-offer" className="border border-sky-500/40 bg-[#0B1220] p-8 max-w-2xl">
          <p className="text-xs tracking-[0.25em] uppercase text-sky-400">{it ? "Abbonamento Guardian" : "Guardian subscription"}</p>
          <div className="flex items-baseline gap-2 mt-4">
            <span className="font-mono text-4xl text-white">€{plan?.amount_eur ?? 1350}</span>
            <span className="text-sm text-slate-500">{it ? "/ 12 mesi — solo annuale" : "/ 12 months — annual only"}</span>
          </div>
          <ul className="space-y-2.5 mt-6 text-sm text-slate-300">
            {[it ? "Segnale Guardian in tempo reale ogni sera (~22:00)" : "Real-time Guardian signal every evening (~22:00)",
              it ? "Operazione in corso in chiaro: direzione, ingresso, stop e target" : "Ongoing operation in clear: direction, entry, stop and target",
              it ? "Storico completo 2009-2026 e P/L trade per trade" : "Full 2009-2026 history and trade-by-trade P/L",
              it ? "Profit lock e circuit breaker sempre visibili" : "Profit lock and circuit breaker always visible"].map((f, i) => (
              <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />{f}</li>
            ))}
          </ul>
          <p className="text-xs text-amber-300/80 mt-4 border border-amber-500/20 bg-amber-500/5 px-3 py-2">
            {it ? "Per questo sistema il trial gratuito non è ammesso: l'abbonamento è esclusivamente annuale." : "The free trial is not available for this system: the subscription is annual only."}
          </p>
          <button data-testid="guardian-checkout-btn" onClick={checkout} disabled={busy}
            className="w-full mt-6 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-black font-medium py-3 text-sm flex items-center justify-center gap-2 transition-colors">
            <CreditCard className="w-4 h-4" /> {it ? "Paga con carta / SEPA" : "Pay with card / SEPA"}
          </button>
          <button data-testid="guardian-crypto-btn" onClick={() => (user ? setCryptoOpen(true) : navigate("/login"))}
            className="w-full mt-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 py-3 text-sm flex items-center justify-center gap-2 transition-colors">
            <Bitcoin className="w-4 h-4" /> {it ? "Paga in crypto" : "Pay with crypto"}
          </button>
        </div>

        <p className="text-[11px] text-slate-500 leading-relaxed max-w-3xl">
          {it
            ? "Risultati da backtest 2009-2026 su dati EOD reali con fill conservativi (in caso di stop loss e take profit toccati nello stesso giorno viene assunto lo scenario peggiore). Le performance passate non garantiscono risultati futuri. Capitale di riferimento €10.000."
            : "Results from a 2009-2026 backtest on real EOD data with conservative fills (if stop loss and take profit are touched on the same day, the worst case is assumed). Past performance does not guarantee future results. Reference capital €10,000."}
        </p>
      </section>
      {cryptoOpen && plan && <CryptoModal plan={plan} onClose={() => setCryptoOpen(false)} />}
    </div>
  );
}
